(() => {
  const elementName = 'written-in-vedas';

  if (customElements.get(elementName)) return;

  class WrittenInVedas extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel]');
      this.track = this.querySelector('[data-track]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.delay = Math.max(2000, Number(this.dataset.autoplayDelay) || 3000);
      this.index = 0;
      this.timer = null;
      this.pointerStart = null;
      this.resizeFrame = null;

      if (!this.viewport || !this.track || this.cards.length < 1) return;

      this.onPrevious = () => this.move(-1, true);
      this.onNext = () => this.move(1, true);
      this.onKeydown = (event) => {
        if (!this.mobile.matches) return;
        if (event.key === 'ArrowLeft') this.move(-1, true);
        if (event.key === 'ArrowRight') this.move(1, true);
      };
      this.onPointerDown = (event) => {
        if (!this.mobile.matches || event.pointerType === 'mouse' && event.button !== 0) return;
        this.pointerStart = event.clientX;
        this.track.classList.add('is-dragging');
        this.stop();
      };
      this.onPointerUp = (event) => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = null;
        this.track.classList.remove('is-dragging');
        if (Math.abs(distance) > 42) this.move(distance < 0 ? 1 : -1, true);
        else this.goTo(this.index);
        this.start();
      };
      this.onPointerCancel = () => {
        this.pointerStart = null;
        this.track.classList.remove('is-dragging');
        this.goTo(this.index);
        this.start();
      };
      this.onVisibility = () => document.hidden ? this.stop() : this.start();
      this.onResize = () => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => this.refresh());
      };

      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goTo(index);
          this.restart();
        });
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
      }

      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-card]');
        if (!card) return;
        const cardIndex = this.cards.indexOf(card);
        if (cardIndex > -1) this.goTo(cardIndex);
        this.stop();
      });

      this.addEventListener('shopify:block:deselect', () => this.start());
      this.refresh();
    }

    disconnectedCallback() {
      this.stop();
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this.onResize);
    }

    refresh() {
      if (!this.mobile.matches) {
        this.stop();
        this.track.style.transform = '';
        return;
      }
      this.index = Math.min(this.index, this.cards.length - 1);
      this.goTo(this.index, false);
      this.start();
    }

    move(direction, userInitiated = false) {
      if (!this.mobile.matches || this.cards.length < 2) return;
      let nextIndex = this.index + direction;
      if (this.loop) nextIndex = (nextIndex + this.cards.length) % this.cards.length;
      else nextIndex = Math.max(0, Math.min(nextIndex, this.cards.length - 1));
      this.goTo(nextIndex);
      if (userInitiated) this.restart();
    }

    goTo(index, animate = true) {
      if (!this.mobile.matches || !this.cards[index]) return;
      this.index = index;
      const card = this.cards[index];
      const viewportWidth = this.viewport.clientWidth;
      const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
      const translate = (viewportWidth / 2) - cardCenter;
      if (!animate) this.track.style.transitionDuration = '0ms';
      this.track.style.transform = `translate3d(${translate}px, 0, 0)`;
      if (!animate) {
        this.track.getBoundingClientRect();
        this.track.style.transitionDuration = '';
      }
      this.updateControls();
    }

    updateControls() {
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (!this.loop) {
        if (this.previous) this.previous.disabled = this.index === 0;
        if (this.next) this.next.disabled = this.index === this.cards.length - 1;
      }
    }

    start() {
      this.stop();
      if (!this.mobile.matches || !this.autoplay || this.cards.length < 2 || document.hidden) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = window.setInterval(() => this.move(1), this.delay);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define(elementName, WrittenInVedas);
})();

